/**
 * Marketing > Campaigns > Contacts.
 *
 * Two views of who the portal will and will not email:
 *
 *  - The contact list: every address the portal can reach, one row per person,
 *    with the date they first appeared and whether they have opted out. Filtered
 *    by the date range they were added, and exportable.
 *  - Opt-outs: the people kept off every campaign, whether they unsubscribed in
 *    ActiveCampaign, bounced, or were removed by staff.
 *
 * The contact list is built from the same sources as a portal-built campaign
 * audience, so its total is the number of people a campaign could reach.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUDIENCE_SOURCE_LABELS,
  OPT_OUT_LABELS,
  addOptOut,
  fetchContactDirectory,
  fetchOptOuts,
  pullOptOutsNow,
  releaseOptOut,
  type AudienceSource,
  type ContactStatusFilter,
  type DirectoryContact,
  type OptOutRow,
  type OptOutStatus,
} from '../../api/campaigns';
import { buildCsv, downloadCsv } from '../../utils/csv';
import '../MarketingOptOutBadge.css';

const PAGE_SIZE = 50;
/** The server caps a page at this many rows, which bounds an export. */
const EXPORT_LIMIT = 25000;
const PORTAL_TZ = 'America/Los_Angeles';

type View = 'contacts' | 'optouts';

const SOURCE_NOTES: Record<'webhook' | 'sync' | 'manual', string> = {
  webhook: 'ActiveCampaign (live)',
  sync: 'ActiveCampaign (history)',
  manual: 'Added by staff',
};

/** A calendar date in the portal's time zone, so the table and the export agree with the date filter. */
const portalDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: PORTAL_TZ }) : '';

const displayDate = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        timeZone: PORTAL_TZ,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

/** YYYY-MM-DD for a date `daysAgo` days back, in the browser's calendar. */
function isoDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const Pager: React.FC<{
  offset: number;
  count: number;
  total: number;
  onPage: (offset: number) => void;
}> = ({ offset, count, total, onPage }) => {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="contacts-pager">
      <span>
        {offset + 1}–{offset + count} of {total.toLocaleString()}
      </span>
      <button
        type="button"
        className="secondary-button"
        disabled={offset === 0}
        onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))}
      >
        Previous
      </button>
      <button
        type="button"
        className="secondary-button"
        disabled={offset + count >= total}
        onClick={() => onPage(offset + PAGE_SIZE)}
      >
        Next
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Contact list
// ---------------------------------------------------------------------------

const ContactList: React.FC<{ userId: number }> = ({ userId }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ContactStatusFilter>('all');
  const [source, setSource] = useState<AudienceSource | ''>('');
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<DirectoryContact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const debouncedSearch = useDebounced(search.trim(), 300);
  const requestId = useRef(0);

  const rangeInvalid = !!from && !!to && from > to;

  // Any change to the filters starts again from the first page.
  useEffect(() => {
    setOffset(0);
  }, [from, to, debouncedSearch, status, source]);

  useEffect(() => {
    if (rangeInvalid) return;
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    fetchContactDirectory(userId, {
      from: from || undefined,
      to: to || undefined,
      search: debouncedSearch || undefined,
      status,
      sources: source ? [source] : undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((page) => {
        if (id !== requestId.current) return;
        setRows(page.contacts);
        setTotal(page.total);
      })
      .catch((e) => {
        if (id !== requestId.current) return;
        setError(e instanceof Error ? e.message : 'Failed to load the contact list.');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [userId, from, to, debouncedSearch, status, source, offset, rangeInvalid]);

  const applyPreset = (days: number | null) => {
    if (days == null) {
      setFrom('');
      setTo('');
    } else {
      setFrom(isoDaysAgo(days));
      setTo('');
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const all = await fetchContactDirectory(userId, {
        from: from || undefined,
        to: to || undefined,
        search: debouncedSearch || undefined,
        status,
        sources: source ? [source] : undefined,
        limit: EXPORT_LIMIT,
        offset: 0,
      });
      const csv = buildCsv(
        [
          'Email',
          'First name',
          'Last name',
          'Company',
          'Phone',
          'Source',
          'Date added',
          'Marketing status',
          'Opt-out reason',
          'Opt-out date',
        ],
        all.contacts.map((c) => [
          c.email,
          c.firstName,
          c.lastName,
          c.company,
          c.phone,
          AUDIENCE_SOURCE_LABELS[c.source],
          portalDate(c.addedAt),
          c.optOutReason ? 'Opted out' : 'Can email',
          c.optOutReason ? OPT_OUT_LABELS[c.optOutReason] : '',
          portalDate(c.optOutAt),
        ])
      );
      const range = from || to ? `_${from || 'start'}_to_${to || 'today'}` : '';
      downloadCsv(`contacts${range}`, csv);
      if (all.total > all.contacts.length) {
        setError(
          `The file holds the first ${all.contacts.length.toLocaleString()} of ${all.total.toLocaleString()} contacts. Narrow the date range to export the rest.`
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export the contact list.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="contacts-filters">
        <label className="contacts-field">
          <span>Added from</span>
          <input
            type="date"
            className="form-input"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="contacts-field">
          <span>Added to</span>
          <input
            type="date"
            className="form-input"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <div className="website-lead-filters" role="group" aria-label="Date range shortcuts">
          <button type="button" className="website-lead-filter" onClick={() => applyPreset(7)}>
            Last 7 days
          </button>
          <button type="button" className="website-lead-filter" onClick={() => applyPreset(30)}>
            Last 30 days
          </button>
          <button type="button" className="website-lead-filter" onClick={() => applyPreset(90)}>
            Last 90 days
          </button>
          <button type="button" className="website-lead-filter" onClick={() => applyPreset(null)}>
            All time
          </button>
        </div>
      </div>

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search name, company or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search contacts"
        />
        <select
          className="form-input contacts-select"
          value={source}
          onChange={(e) => setSource(e.target.value as AudienceSource | '')}
          aria-label="Filter by source"
        >
          <option value="">All sources</option>
          {(Object.keys(AUDIENCE_SOURCE_LABELS) as AudienceSource[]).map((s) => (
            <option key={s} value={s}>
              {AUDIENCE_SOURCE_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          className="form-input contacts-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as ContactStatusFilter)}
          aria-label="Filter by marketing status"
        >
          <option value="all">Everyone</option>
          <option value="mailable">Can email</option>
          <option value="opted_out">Opted out</option>
        </select>
        <button
          type="button"
          className="primary-button campaign-new-button"
          disabled={exporting || rangeInvalid || total === 0}
          onClick={() => void exportCsv()}
        >
          {exporting ? 'Exporting…' : `Export CSV${total ? ` (${total.toLocaleString()})` : ''}`}
        </button>
      </div>

      {rangeInvalid ? (
        <p className="create-order-error" role="alert">
          The start date is after the end date.
        </p>
      ) : null}
      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}

      <p className="campaign-hint">
        {loading ? 'Loading…' : `${total.toLocaleString()} ${total === 1 ? 'contact' : 'contacts'}`}
        {from || to ? ` added ${from ? `from ${from} ` : ''}${to ? `to ${to}` : 'onward'}` : ''}. The date is
        when the address first appeared in the portal, from any source.
      </p>

      <div className="events-table-wrap">
        <table className="tasks-search-results-table website-lead-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Name</th>
              <th scope="col">Company</th>
              <th scope="col">Source</th>
              <th scope="col">Added</th>
              <th scope="col">Marketing</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="events-empty-cell">
                  {loading ? 'Loading contacts…' : 'No contacts match these filters.'}
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <tr key={c.email}>
                  <td data-label="Email">{c.email}</td>
                  <td data-label="Name">{`${c.firstName} ${c.lastName}`.trim() || '—'}</td>
                  <td data-label="Company">{c.company || '—'}</td>
                  <td data-label="Source">{AUDIENCE_SOURCE_LABELS[c.source]}</td>
                  <td data-label="Added">{displayDate(c.addedAt)}</td>
                  <td data-label="Marketing">
                    {c.optOutReason ? (
                      <span className="marketing-opt-out-badge" title={`Since ${displayDate(c.optOutAt)}`}>
                        {OPT_OUT_LABELS[c.optOutReason]}
                      </span>
                    ) : (
                      <span className="website-lead-muted">Can email</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pager offset={offset} count={rows.length} total={total} onPage={setOffset} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Opt-outs
// ---------------------------------------------------------------------------

const OptOutList: React.FC<{ userId: number }> = ({ userId }) => {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<OptOutRow[]>([]);
  const [total, setTotal] = useState(0);
  const [syncStatus, setSyncStatus] = useState<OptOutStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const debouncedSearch = useDebounced(search.trim(), 300);
  const requestId = useRef(0);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    fetchOptOuts(userId, { search: debouncedSearch || undefined, limit: PAGE_SIZE, offset })
      .then((page) => {
        if (id !== requestId.current) return;
        setRows(page.suppressions);
        setTotal(page.total);
        setSyncStatus(page.status);
        setError('');
      })
      .catch((e) => {
        if (id !== requestId.current) return;
        setError(e instanceof Error ? e.message : 'Failed to load the opt-out list.');
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [userId, debouncedSearch, offset, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () =>
    run(async () => {
      await addOptOut(userId, newEmail.trim(), newNote.trim());
      setNotice(`${newEmail.trim().toLowerCase()} will be left out of every campaign.`);
      setNewEmail('');
      setNewNote('');
    });

  const handleRelease = (row: OptOutRow) => {
    if (
      !window.confirm(
        `Allow campaigns to email ${row.email} again? Only do this if they asked to hear from us. ` +
          'They stay unsubscribed in ActiveCampaign until they resubscribe there.'
      )
    ) {
      return;
    }
    void run(async () => {
      await releaseOptOut(userId, row.email);
      setNotice(`${row.email} can be emailed again.`);
    });
  };

  const handlePull = () =>
    run(async () => {
      const result = await pullOptOutsNow(userId);
      setNotice(
        result.added > 0
          ? `Added ${result.added} ${result.added === 1 ? 'person' : 'people'} from ActiveCampaign (${result.scanned.toLocaleString()} checked).`
          : `Nothing new in ActiveCampaign (${result.scanned.toLocaleString()} checked).`
      );
    });

  const exportCsv = async () => {
    setBusy(true);
    setError('');
    try {
      const all = await fetchOptOuts(userId, {
        search: debouncedSearch || undefined,
        limit: EXPORT_LIMIT,
        offset: 0,
      });
      const csv = buildCsv(
        ['Email', 'Reason', 'Recorded from', 'Date', 'Note'],
        all.suppressions.map((s) => [
          s.email,
          OPT_OUT_LABELS[s.reason],
          SOURCE_NOTES[s.source],
          portalDate(s.suppressed_at),
          s.detail,
        ])
      );
      downloadCsv('marketing-opt-outs', csv);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to export the opt-out list.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <p className="campaign-hint">
        Everyone here is left out of every campaign, whichever list or audience it uses. People
        appear when they unsubscribe or hard-bounce in ActiveCampaign, or when staff add them.
        {syncStatus
          ? ` ${syncStatus.webhookRegistered ? 'Live updates are connected' : 'Live updates are not connected'}${
              syncStatus.lastPullAt ? `; last full check ${displayDate(syncStatus.lastPullAt)}` : ''
            }.`
          : ''}
      </p>

      <div className="contacts-add">
        <input
          type="email"
          className="form-input"
          placeholder="Email address to leave out"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          aria-label="Email address to leave out"
        />
        <input
          type="text"
          className="form-input"
          placeholder="Note (optional)"
          value={newNote}
          maxLength={200}
          onChange={(e) => setNewNote(e.target.value)}
          aria-label="Note"
        />
        <button
          type="button"
          className="secondary-button"
          disabled={busy || !newEmail.trim()}
          onClick={() => void handleAdd()}
        >
          Add opt-out
        </button>
      </div>

      <div className="website-lead-controls">
        <input
          type="search"
          className="website-lead-search"
          placeholder="Search email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search opt-outs"
        />
        <button type="button" className="secondary-button" disabled={busy} onClick={() => void handlePull()}>
          Check ActiveCampaign now
        </button>
        <button
          type="button"
          className="primary-button campaign-new-button"
          disabled={busy || total === 0}
          onClick={() => void exportCsv()}
        >
          {`Export CSV${total ? ` (${total.toLocaleString()})` : ''}`}
        </button>
      </div>

      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="campaign-notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="events-table-wrap">
        <table className="tasks-search-results-table website-lead-table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Reason</th>
              <th scope="col">Recorded from</th>
              <th scope="col">Date</th>
              <th scope="col">Note</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="events-empty-cell">
                  {loading ? 'Loading opt-outs…' : 'Nobody has opted out yet.'}
                </td>
              </tr>
            ) : (
              rows.map((s) => (
                <tr key={s.email}>
                  <td data-label="Email">{s.email}</td>
                  <td data-label="Reason">
                    <span className="marketing-opt-out-badge">{OPT_OUT_LABELS[s.reason]}</span>
                  </td>
                  <td data-label="Recorded from">{SOURCE_NOTES[s.source]}</td>
                  <td data-label="Date">{displayDate(s.suppressed_at)}</td>
                  <td data-label="Note">{s.detail || '—'}</td>
                  <td data-label="Actions" className="campaign-row-actions">
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() => handleRelease(s)}
                    >
                      Allow again
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pager offset={offset} count={rows.length} total={total} onPage={setOffset} />
    </>
  );
};

// ---------------------------------------------------------------------------

const ContactsSection: React.FC<{ userId: number }> = ({ userId }) => {
  const [view, setView] = useState<View>('contacts');
  const select = useCallback((v: View) => setView(v), []);

  return (
    <>
      <p className="page-subtitle website-lead-intro">
        Who the portal can email, and who it keeps off every campaign.
      </p>
      <div className="website-lead-filters contacts-view-switch" role="group" aria-label="Contacts view">
        <button
          type="button"
          className={`website-lead-filter${view === 'contacts' ? ' website-lead-filter--active' : ''}`}
          onClick={() => select('contacts')}
        >
          Contact list
        </button>
        <button
          type="button"
          className={`website-lead-filter${view === 'optouts' ? ' website-lead-filter--active' : ''}`}
          onClick={() => select('optouts')}
        >
          Opt-outs
        </button>
      </div>
      {view === 'contacts' ? <ContactList userId={userId} /> : <OptOutList userId={userId} />}
    </>
  );
};

export default ContactsSection;
